import logger from '@/lib/clientLogger';
import { SEEKER_ROUTES, SEEKER_IN_FACILITY_ROUTES } from '@/constants';
import { SeekerState, SeekerPages, SeekerInFacilityPages } from '@/types/seeker';
import { captureMessage } from '@sentry/nextjs';
import { AppState } from '@/types/app';

const seekerInFacilityPathnameMapping: { [key: string]: SeekerInFacilityPages } = {
  [SEEKER_IN_FACILITY_ROUTES.COMMUNITY_LIST]: SeekerInFacilityPages.COMMUNITY_LIST,
  [SEEKER_IN_FACILITY_ROUTES.CARING_LEADS]: SeekerInFacilityPages.CARING_LEADS,
};

const seekerPathnameMapping: { [key: string]: SeekerPages } = {
  [SEEKER_ROUTES.LOCATION]: SeekerPages.LOCATION,
  [SEEKER_ROUTES.CARE_TYPE]: SeekerPages.CARE_TYPE,
  [SEEKER_ROUTES.HELP_TYPE]: SeekerPages.HELP_TYPE,
  [SEEKER_ROUTES.RECAP]: SeekerPages.RECAP,
  [SEEKER_ROUTES.ACCOUNT_CREATION]: SeekerPages.ACCOUNT_CREATION,
  [SEEKER_ROUTES.ACCOUNT_CREATION_NAME]: SeekerPages.ACCOUNT_CREATION_NAME,
};

export function doesSeekerStateHaveDefaultKeys(state: SeekerState) {
  return (
    state &&
    'city' in state &&
    'helpTypes' in state &&
    'state' in state &&
    'typeOfCare' in state &&
    'zipcode' in state
  );
}

export function validateSeekerPreAccount(state: SeekerState, pageSource: string): boolean {
  if (!doesSeekerStateHaveDefaultKeys(state)) {
    logger.error({
      event: 'invalidSeekerStateDefaultKeysMissing',
      source: pageSource,
      invalidState: state,
    });
    captureMessage('invalidSeekerStateDefaultKeysMissing', 'error');
    return false;
  }

  if (
    pageSource === SeekerPages.RECAP ||
    pageSource === SeekerPages.ACCOUNT_CREATION ||
    pageSource === SeekerPages.ACCOUNT_CREATION_NAME
  ) {
    // check location page zipcode
    if (!state.zipcode) {
      logger.error({ event: 'invalidSeekerZipCode', source: pageSource, invalidState: state });
      captureMessage('invalidSeekerZipCode', 'error');
      return false;
    }
  }

  if (
    pageSource === SeekerPages.HELP_TYPE ||
    pageSource === SeekerPages.RECAP ||
    pageSource === SeekerPages.ACCOUNT_CREATION ||
    pageSource === SeekerPages.ACCOUNT_CREATION_NAME
  ) {
    if (!state.typeOfCare) {
      logger.error({ event: 'invalidSeekerTypeOfCare', source: pageSource, invalidState: state });
      captureMessage('invalidSeekerTypeOfCare', 'error');
      return false;
    }
  }

  if (
    pageSource === SeekerPages.RECAP ||
    pageSource === SeekerPages.ACCOUNT_CREATION ||
    pageSource === SeekerPages.ACCOUNT_CREATION_NAME
  ) {
    // checking help-type page
    if (!state.helpTypes) {
      logger.error({ event: 'invalidSeekerHelpTypes', source: pageSource, invalidState: state });
      captureMessage('invalidSeekerHelpTypes', 'error');
      return false;
    }
  }

  return true;
}

export function validateSeekerInFacility(state: AppState, pageSource: string): boolean {
  if (!doesSeekerStateHaveDefaultKeys(state.seeker)) {
    logger.error({
      event: 'invalidSeekerStateDefaultKeysMissing',
      source: pageSource,
      invalidState: state,
    });
    captureMessage('invalidSeekerStateDefaultKeysMissing', 'error');
    return false;
  }

  if (
    pageSource === SeekerInFacilityPages.COMMUNITY_LIST ||
    pageSource === SeekerInFacilityPages.CARING_LEADS
  ) {
    if (!state.flow.memberId && !state.flow.userHasAccount) {
      logger.error({ event: 'userNotAuthenticated', source: pageSource, invalidState: state });
      captureMessage('userNotAuthenticated', 'error');
      return false;
    }
  }

  return true;
}

export function validate(state: AppState, pathName: string): boolean {
  const pageSource: SeekerPages = seekerPathnameMapping[pathName];
  const isInFacilitySource: SeekerInFacilityPages = seekerInFacilityPathnameMapping[pathName];

  if (isInFacilitySource) {
    return validateSeekerInFacility(state, isInFacilitySource);
  }

  if (pageSource) {
    return validateSeekerPreAccount(state.seeker, pageSource);
  }
  return true;
}
